/**
 * SiteModals — Guided Onboarding / Create Site / Edit Site modals for SitesPage.
 *
 * JSX moved verbatim from src/components/SitesPage.tsx (SitesPage split 3/3).
 * Rendered DOM is byte-equivalent (classNames, text, conditional structure, keys unchanged).
 * All page state/handlers arrive as props; the interface + SitesPage invocation are
 * generated from a single prop list so they cannot drift.
 */
import React from 'react';
import { CreateAssetRequest } from '../../services/AssetService';

interface SiteModalsProps {
  creatingGuidedSite: boolean;
  editAsset: CreateAssetRequest;
  newAsset: CreateAssetRequest;
  showCreateModal: boolean;
  showEditModal: boolean;
  showGuidedCreateModal: boolean;
  t: (key: string, fallback?: string) => string;
  setEditAsset: React.Dispatch<React.SetStateAction<CreateAssetRequest>>;
  setNewAsset: React.Dispatch<React.SetStateAction<CreateAssetRequest>>;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowGuidedCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreateAsset: () => Promise<void>;
  handleCreateGuidedSite: () => Promise<void>;
  handleEditAsset: () => Promise<void>;
}

const SiteModals: React.FC<SiteModalsProps> = ({
  creatingGuidedSite,
  editAsset,
  newAsset,
  showCreateModal,
  showEditModal,
  showGuidedCreateModal,
  t,
  setEditAsset,
  setNewAsset,
  setShowCreateModal,
  setShowEditModal,
  setShowGuidedCreateModal,
  handleCreateAsset,
  handleCreateGuidedSite,
  handleEditAsset,
}) => {
  return (
    <>
      {/* Guided Onboarding Site Modal */}
      {showGuidedCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Create Guided Onboarding Site</h2>
              <input
                type="text"
                placeholder={t('siteName', 'Site Name')}
                value={newAsset.name}
                onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                className="form-input"
              />
              <textarea
                placeholder={t('siteDescription', 'Site Description')}
                value={newAsset.description}
                onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                className="form-textarea"
              />
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                This will create the site and open its onboarding checklist inside the Sites page.
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowGuidedCreateModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleCreateGuidedSite} className="btn btn-primary" disabled={creatingGuidedSite}>
                  {creatingGuidedSite ? 'Creating...' : 'Create Guided Site'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Site Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>{t('createNewSite', 'Create New Site')}</h2>
            <input
              type="text"
              placeholder={t('siteName', 'Site Name')}
              value={newAsset.name}
              onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
              className="form-input"
            />
            <textarea
              placeholder={t('siteDescription', 'Site Description')}
              value={newAsset.description}
              onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
              className="form-textarea"
            />
              <div className="modal-actions">
                <button onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleCreateAsset} className="btn btn-primary">
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Site Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>{t('editSite', 'Edit Site')}</h2>
            <input
              type="text"
              placeholder={t('siteName', 'Site Name')}
              value={editAsset.name}
              onChange={(e) => setEditAsset({ ...editAsset, name: e.target.value })}
              className="form-input"
            />
            <textarea
              placeholder={t('siteDescription', 'Site Description')}
              value={editAsset.description}
              onChange={(e) => setEditAsset({ ...editAsset, description: e.target.value })}
              className="form-textarea"
            />
              <div className="modal-actions">
                <button onClick={() => setShowEditModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleEditAsset} className="btn btn-primary">
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteModals;
